import { ApiError } from '@/api/client';

/** Shown when a submission fails because of data entry (client feedback wording). */
export const SUBMIT_FAIL = 'Unable to submit. Please confirm all data entry errors are cleared and submit again.';
/** Shown when a submission fails for a reason that is not the data (server / connection). */
export const SUBMIT_RETRY = 'Unable to submit. Please try again in a few minutes.';

export const REQUIRED = 'Required';
export const BAD_EMAIL = 'Enter a valid email';

/** Empty or only spaces (Maconomy stores a blank text field as a single space). */
export const blank = (v?: unknown) => !String(v ?? '').trim();

/** The API's field-level rejection (zod), as opposed to a specific business message. */
export function isValidationError(e: unknown): boolean {
  return e instanceof ApiError && e.status === 400 && e.problem.title === 'Validation failed';
}

/** Toast text for a failed submission. Specific API messages (e.g. "Termination Reason is
 *  required", "data load in progress") are kept; the generic validation failure gets the
 *  client wording; server / network failures get the retry wording. */
export function submitErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return SUBMIT_RETRY;
  if (isValidationError(e)) return SUBMIT_FAIL;
  if (e.status >= 500 && e.status !== 503) return SUBMIT_RETRY;
  return e.problem.title || SUBMIT_FAIL;
}

/** Field errors from the API's validation response, keyed by form field name. */
export function apiFieldErrors(e: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isValidationError(e) || !Array.isArray((e as ApiError).problem.detail)) return out;
  for (const issue of (e as ApiError).problem.detail as { path?: string; message?: string }[]) {
    const key = String(issue.path ?? '').split('.')[0];
    if (!key || out[key]) continue;
    const msg = String(issue.message ?? '');
    out[key] = /required|at least 1|empty/i.test(msg) ? REQUIRED : msg.includes('email') ? BAD_EMAIL : 'Check this field';
  }
  return out;
}

/** Scroll the first highlighted field into view (after the highlight renders). */
export function scrollToFirstError(): void {
  setTimeout(() => {
    document.querySelector('[data-field-error]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 50);
}
